import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user.types';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user status is active
  if (user.status !== 'active') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <h2>{t('auth.accountInactive')}</h2>
        <p>{t('auth.accountInactiveMsg')}</p>
      </Box>
    );
  }

  // Agentes (PFAGT) só podem aceder rotas do portal do agente
  if (user.role === 'PFAGT' && !allowedRoles?.includes('PFAGT')) {
    return <Navigate to="/agente/dashboard" replace />;
  }

  // Entidades e perfis de negócio só acedem ao que lhes é permitido
  const ENTITY_ROLES = ['PFENT', 'PFLOJ', 'PFEMP', 'PFGOV', 'PFONG', 'PFEVT', 'PFPRO', 'PFFOD', 'PFDEV', 'PFCON'];
  if (ENTITY_ROLES.includes(user.role) && allowedRoles && allowedRoles.length > 0 && !allowedRoles.some(r => ENTITY_ROLES.includes(r) || r === user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check role authorization if roles are specified
  if (allowedRoles && allowedRoles.length > 0) {
    const hasPermission = allowedRoles.includes(user.role);

    if (!hasPermission) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <h2>{t('auth.accessDenied')}</h2>
          <p>{t('auth.accessDeniedMsg')}</p>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
